<template>
  <div class="pa-4">
    <RichText
      v-model="store.plan.raci"
      label="2.1 RACI"
      class="mb-4"
      required
      :readonly="readonly"
    />

    <RACIMatrix
      :plan="store.plan"
      :readonly="readonly"
      @add:task="addTask()"
      @add:role="addRole()"
      @delete:task="deleteTask($event)"
      @delete:role="deleteRole($event)"
      @set:raci="setRACI($event.task, $event.role, $event.value)"
    />
  </div>
</template>

<script setup lang="ts">
import { RACIData, RACIRole, RACITask } from "@/classes";
import type { Deleteable, RACIValue } from "@/classes/asset-management-plan";

import RichText from "@/components/base/inputs/RichText.vue";

import RoleAutocomplete from "../RoleAutocomplete.vue";
import { useAMPStore } from "@/store/asset-management-plan";
import RACIMatrix from "../raci/RACIMatrix.vue";

const props = defineProps<{
  readonly: boolean;
}>();

const emit = defineEmits<{
  (e: "delete", val: Deleteable): void;
}>();

const store = useAMPStore();

const addTask = () => {
  const task = new RACITask();
  task.asset_management_plan = store.plan;
  task.asset_management_plan_id = store.plan.id;
  store.plan.raci_tasks.push(task);
};

const addRole = () => {
  const role = new RACIRole();
  role.asset_management_plan = store.plan;
  role.asset_management_plan_id = store.plan.id;
  store.plan.raci_roles.push(role);
};

const deleteTask = (task: RACITask) => {
  store.plan.raci_tasks = store.plan.raci_tasks.filter((t) => t.id !== task.id);
  emit("delete", task);
  const relatedData = store.plan.raci_data.filter(
    (d) => d.raci_task_id === task.id,
  );
  for (const data of relatedData) deleteData(data);
};
const deleteRole = (role: RACIRole) => {
  store.plan.raci_roles = store.plan.raci_roles.filter((r) => r.id !== role.id);
  emit("delete", role);
  const relatedData = store.plan.raci_data.filter(
    (d) => d.raci_role_id === role.id,
  );
  for (const data of relatedData) deleteData(data);
};

const deleteData = (data: RACIData) => {
  store.plan.raci_data = store.plan.raci_data.filter((d) => d.id !== data.id);
  emit("delete", data);
};

const setRACI = (task: RACITask, role: RACIRole, value: RACIValue | null) => {
  const data = store.plan.raci_data.find(
    (d) => d.raci_role_id === role.id && d.raci_task_id === task.id,
  );

  if (data) {
    data.value = value;
  } else {
    const newData = new RACIData();
    newData.asset_management_plan = store.plan;
    newData.asset_management_plan_id = store.plan.id;
    newData.raci_role_id = role.id;
    newData.raci_task_id = task.id;
    newData.value = value;
    store.plan.raci_data.push(newData);
  }
};
</script>
